import { works } from "@/api/works/useWorkQuery";
import { formatNumberCell } from "@/features/inventory/components/InventoryActionHeader";
import {
  NoDataTr,
  PaginationWrapper,
  TabBodyTable,
  TabBodyTitle,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  WorkTabBodyContainer,
} from "@/features/work/workView/WorkProductionHistory";
import { dataStyle, excelDownLoad, headerStyle, headerTitleStyle } from "@/utils/excelDownLoad";
import { setToLocaleString } from "@/utils/unitMark";
import { Button, Flex, Pagination, Text } from "@mantine/core";
import {
  ProductionPlansGet200ResponseRowsInnerWorksInner,
  ProductionPlansGet200ResponseRowsInnerWorksInnerInputSummary,
} from "@sizlcorp/sizl-api-document/dist/models";
import { IconFileSpreadsheet } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useState } from "react";
import { useTranslation } from "react-i18next";

interface WorkInputLotProps {
  workId: number | undefined;
}

export const WorkInputLot = ({ workId }: WorkInputLotProps) => {
  const [activePage, setActivePage] = useState<number>(1);
  const { t } = useTranslation();
  const { data: workData } = useQuery(
    works.get({
      query: {
        $and: [{ id: { $eq: workId } }],
      },
      populate: ["inputSummary", "itemWithGrade", "equipment", "summary"],
    }),
  );

  const rows: ProductionPlansGet200ResponseRowsInnerWorksInner | undefined =
    workData?.data?.rows && workData?.data?.rows[0];

  const excelDownload = async () => {
    try {
      const title = [
        {
          v: `원부자재 투입(배합)일지(${rows?.equipmentName ?? ""})`,
          t: "s",
          s: headerTitleStyle,
        },
      ];

      const infoHeader = [
        { v: "작업지시 번호", t: "s", s: headerStyle },
        { v: "품목군", t: "s", s: headerStyle },
        { v: "품목명", t: "s", s: headerStyle },
        { v: "Grade", t: "s", s: headerStyle },
        { v: "Color", t: "s", s: headerStyle },
        { v: "형태", t: "s", s: headerStyle },
        { v: "작업 일자", t: "s", s: headerStyle },
        { v: "목표 수량", t: "s", s: headerStyle },
        { v: "총 생산 수량", t: "s", s: headerStyle },
      ];

      const infoData = [
        { v: rows?.id ?? "", t: "s", s: dataStyle },
        { v: t(rows?.itemWithGrade?.itemType ?? ""), t: "s", s: dataStyle },
        { v: rows?.itemWithGrade?.name, t: "s", s: dataStyle },
        { v: rows?.itemWithGrade?.gradeName ?? "", t: "s", s: dataStyle },
        { v: rows?.itemWithGrade?.itemCategoryName ?? "", t: "s", s: dataStyle },
        { v: rows?.itemWithGrade?.abstractItemCode ?? "", t: "s", s: dataStyle },
        { v: dayjs(rows?.createdAt).format("YYYY-MM-DD hh:mm:ss") ?? "", t: "s", s: dataStyle },
        formatNumberCell(Number(rows?.targetQuantity)),
        formatNumberCell(Number(rows?.summary?.performance)),
      ];

      // 투입 로트 테이블 헤더
      const lotHeaderRow = [
        { v: "품목군", t: "s", s: headerStyle },
        { v: "원부자재명", t: "s", s: headerStyle },
        { v: "Grade명", t: "s", s: headerStyle },
        { v: "Color", t: "s", s: headerStyle },
        { v: "형태", t: "s", s: headerStyle },
        { v: "로트 ID", t: "s", s: headerStyle },
        { v: "로트명", t: "s", s: headerStyle },
        { v: "수량", t: "s", s: headerStyle },
        { v: "비고", t: "s", s: headerStyle },
      ];

      const lotDataRows = Array.isArray(rows?.inputSummary)
        ? rows?.inputSummary.map((lot) => [
            { v: t(lot.itemWithGrade?.itemType ?? ""), t: "s", s: dataStyle },
            { v: lot.itemName ?? "", t: "s", s: dataStyle },
            { v: lot.itemWithGrade?.gradeName ?? "", t: "s", s: dataStyle },
            { v: lot.itemWithGrade?.itemCategoryName ?? "", t: "s", s: dataStyle },
            { v: lot.itemWithGrade?.abstractItemCode ?? "", t: "s", s: dataStyle },
            { v: lot.lotId ?? "", t: "s", s: dataStyle },
            { v: lot.lotName ?? "", t: "s", s: dataStyle },
            formatNumberCell(lot.sumQuantity),
            { v: "", t: "s", s: dataStyle },
          ])
        : [];

      // 최종 구성: 작업지시 정보 + 투입로트 정보
      const fullDataRows = [
        // [],
        infoHeader,
        infoData,
        [], // 빈 줄
        lotHeaderRow,
        ...(lotDataRows || []),
      ];

      await excelDownLoad({
        headerRow: title,
        dataRows: fullDataRows,
        colWidths: [120, 150, 120, 120, 120, 120, 150, 120, 120, 120, 120],
        merges: [
          {
            s: { r: 0, c: 0 },
            e: { r: 0, c: 8 },
          },
        ],
        fileName: `원부자재_투입(배합)일지`,
      });
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <WorkTabBodyContainer>
      <Flex justify="space-between" align="center" mb={16} w="100%">
        <TabBodyTitle>
          <Text fz="xl" fw={500}>
            투입 로트 정보
          </Text>
          <Text fz="xs" fw={400}>
            투입한 정보를 표시합니다.
          </Text>
        </TabBodyTitle>
        <Flex justify="flex-end" align="center" gap="1rem">
          {workData?.data?.rows?.[0].inputTotalQuantity && (
            <Text fz="1.5rem">
              합계 : {setToLocaleString(workData?.data?.rows?.[0].inputTotalQuantity)}
            </Text>
          )}
          <Button leftIcon={<IconFileSpreadsheet />} color="teal" onClick={() => excelDownload()}>
            엑셀 다운로드
          </Button>
        </Flex>
      </Flex>
      <TabBodyTable>
        <Thead>
          <Tr>
            <Th>품목군</Th>
            <Th>원부자재명</Th>
            <Th>Grade명</Th>
            <Th>Color</Th>
            <Th>형태</Th>
            <Th>로트 ID</Th>
            <Th>로트명</Th>
            <Th>수량</Th>
          </Tr>
        </Thead>
        <Tbody>
          {Array.isArray(rows?.inputSummary) && rows?.inputSummary.length ? (
            rows?.inputSummary?.map(
              (input: ProductionPlansGet200ResponseRowsInnerWorksInnerInputSummary) => (
                <Tr key={input.lotId}>
                  <Td>{t(input.itemWithGrade?.itemType ?? "")}</Td>
                  <Td>{input?.itemName}</Td>
                  <Td>{input.itemWithGrade?.gradeName}</Td>
                  <Td>{input.itemWithGrade?.itemCategoryName}</Td>
                  <Td>{input.itemWithGrade?.abstractItemCode}</Td>
                  <Td style={{ justifyContent: "flex-end" }}>{input?.lotId}</Td>
                  <Td>{input?.lotName}</Td>
                  <Td style={{ justifyContent: "flex-end" }}>{input?.sumQuantity}</Td>
                </Tr>
              ),
            )
          ) : (
            <NoDataTr>
              <Text>투입 로트 정보가 존재하지 않습니다.</Text>
            </NoDataTr>
          )}
        </Tbody>
      </TabBodyTable>
      <PaginationWrapper>
        <Pagination
          onChange={setActivePage}
          value={activePage}
          total={workData?.data?.totalPages ?? 0}
          size="lg"
          radius="sm"
        />
      </PaginationWrapper>
    </WorkTabBodyContainer>
  );
};
